import ListenButton from '@/components/atoms/ListenButton/ListenButton';
import GradingState from '@/components/organisms/Grading/GradingState';
import { Button } from '@/components/atoms';
import RecordingGradingContents from '@/components/Modal/RecordingGradingContents';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import { useMemo, useState, useEffect, useRef } from 'react';
import { academyLearningScheduleApi } from 'services/api';

interface GradingPartWritingProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  responseJson?: string
  onManualGradingChange?: (status: 'correct' | 'incorrect' | undefined) => void
  recordingGradingResult?: 'correct' | 'incorrect'
  onRecordingGradingChange?: (status: 'correct' | 'incorrect' | undefined) => void
  manualGradingResult?: 'correct' | 'incorrect' | undefined
  canUseAi?: boolean
  manualLocked?: boolean
  recordingLocked?: boolean
}

const AUTO_CORRECT_THRESHOLD = 80;

const normalizeWord = (value: string) => value.replace(/[^a-z0-9']/gi, '').toLowerCase();

const getTrailingPunctuation = (value: string) => {
  const match = value.trim().match(/[^\w']+$/);
  return match ? match[0] : '';
};

const hasTrailingPunctuationMismatch = (expected: string, actual: string) => {
  const expectedPunct = getTrailingPunctuation(expected);
  if (!expectedPunct) return false;
  const actualPunct = getTrailingPunctuation(actual);
  return actualPunct !== expectedPunct;
};

type WordSpan = {
  start: number;
  end: number;
  text: string;
  normalized: string;
  used: boolean;
};

const MAX_FUZZY_DISTANCE = 2;

const levenshteinDistance = (a: string, b: string) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const prev = new Array(b.length + 1).fill(0).map((_, idx) => idx);
  const curr = new Array(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,      // deletion
        curr[j - 1] + 1,  // insertion
        prev[j - 1] + cost // substitution
      );
    }
    for (let j = 0; j <= b.length; j++) {
      prev[j] = curr[j];
    }
  }

  return curr[b.length];
};

const buildEssaySentenceParts = (sentence: string, rawWords: string[]) => {
  if (!sentence) return [];

  const wordsInfo = rawWords
    .map((w) => {
      const trimmed = (w || '').trim();
      if (!trimmed) return null;
      const match = trimmed.match(/^(.*?)([^\w']+)?$/);
      const target = match ? match[1] || trimmed : trimmed;
      const trailingPunct = match && match[2] ? match[2] : '';
      return { target: target.trim(), trailingPunct };
    })
    .filter(Boolean) as Array<{ target: string; trailingPunct: string }>;

  if (!wordsInfo.length) {
    return [{ text: sentence, isBlank: false }];
  }

  const wordRegex = /\b[\w']+\b/g;
  const wordSpans: WordSpan[] = [];

  let match: RegExpExecArray | null;
  while ((match = wordRegex.exec(sentence)) !== null) {
    const text = match[0];
    wordSpans.push({
      start: match.index,
      end: match.index + text.length,
      text,
      normalized: normalizeWord(text),
      used: false
    });
  }

  if (!wordSpans.length) {
    return [{ text: sentence, isBlank: false }];
  }

  const tokenizeTarget = (target: string) => {
    const matches = target.match(/\b[\w']+\b/g);
    if (!matches) return [];
    return matches.map((token) => normalizeWord(token));
  };

  const findCandidateSpan = (target: string): { start: number; end: number; spans: WordSpan[] } | null => {
    const tokens = tokenizeTarget(target);
    if (!tokens.length) return null;

    let bestMatch: { start: number; end: number; spans: WordSpan[] } | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    const tryMatchAtIndex = (startIdx: number) => {
      const matched: WordSpan[] = [];
      for (let offset = 0; offset < tokens.length; offset++) {
        const candidate = wordSpans[startIdx + offset];
        if (!candidate || candidate.used) return null;
        const dist = levenshteinDistance(candidate.normalized, tokens[offset]);
        if (dist > MAX_FUZZY_DISTANCE) return null;
        matched.push(candidate);
      }

      return {
        spans: matched,
        start: matched[0].start,
        end: matched[matched.length - 1].end
      };
    };

    wordSpans.forEach((span, idx) => {
      if (span.used) return;
      const candidate = tryMatchAtIndex(idx);
      if (!candidate) return;
      const dist = tokens.length === 1
        ? levenshteinDistance(span.normalized, tokens[0])
        : 0;
      if (dist < bestDistance || (dist === bestDistance && candidate.start < (bestMatch?.start ?? Number.MAX_SAFE_INTEGER))) {
        bestMatch = candidate;
        bestDistance = dist;
      }
    });

    return bestMatch;
  };

  const matches: Array<{ start: number; end: number; spans: WordSpan[]; idx: number; trailingPunct: string }> = [];
  wordsInfo.forEach((info, idx) => {
    const candidate = findCandidateSpan(info.target);
    if (!candidate) {
      console.warn('[PART_WRITING] 문장에서 정답 단어를 찾을 수 없습니다:', info.target);
      return;
    }
    candidate.spans.forEach((span) => { span.used = true; });
    matches.push({ ...candidate, idx, trailingPunct: info.trailingPunct });
  });

  if (!matches.length) {
    return [{ text: sentence, isBlank: false }];
  }

  matches.sort((a, b) => a.start - b.start);

  const parts: Array<{ text: string; isBlank: boolean }> = [];
  let cursor = 0;
  matches.forEach((m) => {
    if (m.start > cursor) {
      parts.push({ text: sentence.slice(cursor, m.start), isBlank: false });
    }

    const matchedText = sentence.slice(m.start, m.end);
    parts.push({ text: matchedText, isBlank: true });

    const after = sentence.slice(m.end);
    const trailingMatch = after.match(/^[^\w']+/);
    const trailingInSentence = trailingMatch ? trailingMatch[0] : '';

    if (trailingInSentence) {
      if (m.trailingPunct && trailingInSentence.startsWith(m.trailingPunct)) {
        cursor = m.end + trailingInSentence.length;
      } else {
        parts.push({ text: trailingInSentence, isBlank: false });
        cursor = m.end + trailingInSentence.length;
      }
    } else {
      cursor = m.end;
    }
  });

  if (cursor < sentence.length) {
    parts.push({ text: sentence.slice(cursor), isBlank: false });
  }

  const matchedBlanks = matches.length;
  if (matchedBlanks < wordsInfo.length) {
    const missing = wordsInfo.length - matchedBlanks;
    for (let i = 0; i < missing; i++) {
      parts.push({ text: '', isBlank: true });
    }
  }

  return parts;
};

const buildSentenceWithReplacement = (base: string, target: string, replacement: string) => {
  if (!base || !target) return replacement || base || '';
  const lowerBase = base.toLowerCase();
  const lowerTarget = target.toLowerCase();
  const idx = lowerBase.indexOf(lowerTarget);
  if (idx === -1) return replacement ? `${base} ${replacement}`.trim() : base;
  const prefix = base.slice(0, idx);
  const suffix = base.slice(idx + target.length);
  return `${prefix}${replacement || ''}${suffix}`;
};

const calculateSentenceSimilarity = (expected: string, actual: string) => {
  const expectedWords = expected.split(/\s+/).filter(Boolean);
  const actualWords = actual.split(/\s+/).filter(Boolean);
  const length = Math.max(expectedWords.length, actualWords.length);
  if (length === 0) return null;
  let matched = 0;
  for (let i = 0; i < length; i++) {
    const ew = expectedWords[i] || '';
    const aw = actualWords[i] || '';
    if (normalizeWord(ew) === normalizeWord(aw) && ew) {
      matched += 1;
    }
  }
  const percent = Math.round((matched / length) * 100);
  if (hasTrailingPunctuationMismatch(expected, actual)) {
    return Math.min(percent, 99);
  }
  return percent;
};

const GradingPartWriting = ({ type = 'scoreCard', activity, responseJson, onManualGradingChange, recordingGradingResult, onRecordingGradingChange, manualGradingResult, canUseAi = true, manualLocked = false, recordingLocked = false }: GradingPartWritingProps) => {
  // 실제 데이터 파싱
  const parsed = useMemo(() => {
    let config: any = {};
    try {
      if (activity?.configJson) {
        config = typeof activity.configJson === 'string' ? JSON.parse(activity.configJson) : activity.configJson;
      }
    } catch (e) {
      console.error('PartWriting configJson 파싱 실패:', e);
    }

    let itemContent: any = {};
    try {
      const firstItem = activity?.items?.[0];
      if (firstItem?.contentJson) {
        itemContent = typeof firstItem.contentJson === 'string' ? JSON.parse(firstItem.contentJson) : firstItem.contentJson;
      }
    } catch (e) {
      console.error('PartWriting item contentJson 파싱 실패:', e);
    }

    let response: any = {};
    try {
      if (responseJson) response = JSON.parse(responseJson);
    } catch (e) {
      console.error('PartWriting responseJson 파싱 실패:', e);
    }

    // 확장 케이스(WORD_CHOICE 기반) 처리: wordChoices[0].contentJson에서 문장 추출
    let derivedFullText = '';
    let isWordChoiceExtension = false;
    let parentConfigJson: any = {};
    let itemTitleTranslation = '';

    try {
      if (!config.fullText && itemContent && Array.isArray(itemContent.wordChoices) && itemContent.wordChoices.length > 0) {
        isWordChoiceExtension = true;
        const wc0 = itemContent.wordChoices[0];
        const wc0Content = wc0?.contentJson ? (typeof wc0.contentJson === 'string' ? JSON.parse(wc0.contentJson) : wc0.contentJson) : {};
        derivedFullText = wc0Content.word || wc0Content.sentence || '';
        itemTitleTranslation = wc0Content.titleTranslation || '';
        
        // parentConfigJson 파싱 (정답 부분 추출을 위해)
        if (itemContent.parentConfigJson) {
          parentConfigJson = typeof itemContent.parentConfigJson === 'string' 
            ? JSON.parse(itemContent.parentConfigJson) 
            : itemContent.parentConfigJson;
        }
      }
    } catch (e) {
      console.error('[PART_WRITING] WORD_CHOICE 확장 파싱 에러:', e);
    }

    const fullText: string = config.fullText || itemContent.text || derivedFullText || '';
    const translation: string = config.translation || itemContent.translation || '';
    const audioUrl: string | null = itemContent.audioUrl || config.audioUrl || null;
    const imageUrl: string | null = itemContent.imageUrl || itemContent.wordImageUrl || config.imageUrl || null;
    const correctWords: string[] = Array.isArray(config.correctWords) ? config.correctWords : [];
    const recordingEnabled = !!config.recordingEnabled;

    const inputWords: string[] = Array.isArray(response.inputWords) ? response.inputWords : [];
    const selectedWords: string[] = Array.isArray(response.selectedWords) ? response.selectedWords : [];
    const answerText: string = typeof response.answer === 'string'
      ? response.answer
      : (typeof response.userInput === 'string' ? response.userInput : (typeof response.inputText === 'string' ? response.inputText : (inputWords.length > 0 ? inputWords.join(' ') : (selectedWords.length > 0 ? selectedWords.join(' ') : ''))));
    
    // 녹음 URL 파싱 (WORD_CHOICE 확장은 녹음 없음, 에세이는 녹음 있음)
    const recordingUrl: string | null = isWordChoiceExtension 
      ? null 
      : (typeof response.recordingUrl === 'string' ? response.recordingUrl : null);
      // const isCorrectFlag: boolean | undefined = typeof response.isCorrect === 'boolean' ? response.isCorrect : undefined;

    // 정오답 계산
    // 문장 내 실제 빈칸 순서대로 correctWords 정렬
    const tokens = fullText.split(/(\s+)/);
    // Group correctWords by their normalized form to handle duplicates and matching
    const correctWordsMap: Record<string, string[]> = {};
    correctWords.forEach(w => {
        const norm = normalizeWord(w);
        if (!correctWordsMap[norm]) correctWordsMap[norm] = [];
        correctWordsMap[norm].push(w);
    });

    // 문장에서 빈칸으로 쓰인 단어들을 순서대로 추출 (원본 정답 형태 유지)
    const sortedCorrectWords: string[] = [];
    tokens.forEach(tok => {
      const key = normalizeWord(tok);
      if (key && correctWordsMap[key] && correctWordsMap[key].length > 0) {
        // 매칭된 정답 단어 중 하나를 가져옴 (FIFO)
        const originalWord = correctWordsMap[key].shift(); 
        if (originalWord) sortedCorrectWords.push(originalWord);
      }
    });
    // 만약 문장 분석에서 추출된 단어 수가 correctWords 수와 다르면 (예외 케이스) 원본 순서 사용
    const finalCorrectWords = sortedCorrectWords.length === correctWords.length ? sortedCorrectWords : correctWords;

    let correctCount = 0;
    const parts = finalCorrectWords.map((w, idx) => {
      const user = inputWords[idx] || '';
      // 문장부호 포함하여 비교 (마침표 누락은 오답)
      const ok = user.trim().toLowerCase() === w.trim().toLowerCase();
      if (ok) correctCount += 1;
      return { id: idx, correct: w, user, status: ok ? 'correct' : 'incorrect' as const };
    });

    const overallStatus: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading' =
      inputWords.length === 0 ? 'grading' : (correctCount === correctWords.length ? 'correct' : 'word_incorrect');

    // WORD_CHOICE 확장의 경우 titleTranslation이 정답 부분 (문항별 값 우선)
    const correctAnswerPart = isWordChoiceExtension ? (itemTitleTranslation || parentConfigJson.titleTranslation || '') : '';

    return {
      fullText,
      translation,
      audioUrl,
      imageUrl,
      correctWords,
      inputWords,
      selectedWords,
      parts,
      overallStatus,
      recordingUrl,
      answerText,
      isWordChoiceExtension,
      correctAnswerPart,
      recordingEnabled
    };
  }, [activity, responseJson]);

  const accuracyPercent = useMemo(() => {
    if (parsed.parts && parsed.parts.length > 0) {
      const total = parsed.parts.length;
      const matched = parsed.parts.filter((part: any) => part.status === 'correct').length;
      return total > 0 ? Math.round((matched / total) * 100) : null;
    }
    if (parsed.correctAnswerPart) {
      const correctWords = parsed.correctAnswerPart.split(/\s+/).filter(Boolean);
      const studentPhrase = (parsed.answerText && parsed.answerText.trim().length > 0)
        ? parsed.answerText
        : (parsed.selectedWords && parsed.selectedWords.length > 0 ? parsed.selectedWords.join(' ') : '');
      if (!studentPhrase) return null;
      if (parsed.isWordChoiceExtension && parsed.fullText) {
        const combinedStudent = buildSentenceWithReplacement(parsed.fullText, parsed.correctAnswerPart, studentPhrase);
        const combinedCorrect = buildSentenceWithReplacement(parsed.fullText, parsed.correctAnswerPart, parsed.correctAnswerPart);
        return calculateSentenceSimilarity(combinedCorrect, combinedStudent);
      }
      const studentWords = studentPhrase.split(/\s+/).filter(Boolean);
      const length = Math.max(correctWords.length, studentWords.length);
      if (length === 0) return null;
      const matched = Array.from({ length }).filter((_, idx) => {
        const cw = correctWords[idx] || '';
        const sw = studentWords[idx] || '';
        const baseMatch = normalizeWord(cw) === normalizeWord(sw);
        if (!baseMatch) return false;
        if (idx === length - 1 && hasTrailingPunctuationMismatch(cw, sw)) {
          return false;
        }
        return true;
      }).length;
      const percent = Math.round((matched / length) * 100);
      if (hasTrailingPunctuationMismatch(parsed.correctAnswerPart, studentPhrase)) {
        return Math.min(percent, 99);
      }
      return percent;
    }
    return null;
  }, [parsed.parts, parsed.correctAnswerPart, parsed.answerText, parsed.selectedWords, parsed.isWordChoiceExtension, parsed.fullText]);

  const hasStudentInput =
    (parsed.answerText && parsed.answerText.trim().length > 0) ||
    (parsed.inputWords && parsed.inputWords.some((w) => (w || '').trim().length > 0)) ||
    (parsed.selectedWords && parsed.selectedWords.length > 0);

  const autoWritingStatus: 'correct' | 'word_incorrect' = (() => {
    if (!hasStudentInput) return 'word_incorrect';
    if (accuracyPercent === null) return 'word_incorrect';
    return accuracyPercent >= 100 ? 'correct' : 'word_incorrect';
  })();

  // (이전 렌더러 제거: 뉘앙스도 에세이와 동일 UX 적용)

  // 에세이 빈칸 채우기 보기: 원문장 전체를 그대로 표시 (대체: 뉘앙스인 경우 하이라이트 표시)
  const renderEssayPrompt = () => {
    const text = parsed.fullText || '';
    const translation = parsed.translation || '';
    const correctPart = parsed.correctAnswerPart || '';
    
    if (!text) return <span className='text-gray-500'>보기 문장이 없습니다.</span>;

    const renderText = () => {
      // 뉘앙스 부분 영작(WORD_CHOICE 확장)인 경우 정답 부분 하이라이트
      if (parsed.isWordChoiceExtension && correctPart) {
        const lowerText = text.toLowerCase();
        const lowerPart = correctPart.toLowerCase();
        const idx = lowerText.indexOf(lowerPart);
        if (idx !== -1) {
          const before = text.slice(0, idx);
          const highlighted = text.slice(idx, idx + correctPart.length);
          const after = text.slice(idx + correctPart.length);
          return (
            <span className='text-2xl font-bold text-center text-gray-800'>
              {before}
              <span className='text-blue-500'>{highlighted}</span>
              {after}
            </span>
          );
        }
      }
      // 기본 에세이: 전체 텍스트 그대로 표시
      return <span className='text-2xl font-bold text-center text-gray-800'>{text}</span>;
    };
    
    return (
      <div className='flex flex-col gap-2'>
        <p className='text-center'>{renderText()}</p>
        {translation && (
          <p className='text-base text-center text-gray-500'>{translation}</p>
        )}
      </div>
    );
  };

  // 에세이 빈칸별 학생 답 표시: 원문장 내에서 학생 입력을 정오답 색으로 구분
  const renderEssayStudentAnswers = () => {
    const text = parsed.fullText || '';
    if (!text || !parsed.correctWords || parsed.correctWords.length === 0) {
      return <span className='text-gray-500'>학생 입력이 없습니다.</span>;
    }
    
    const sentenceParts = buildEssaySentenceParts(text, parsed.correctWords);
    const hasBlankParts = sentenceParts.some((part) => part.isBlank);
    if (!hasBlankParts) {
      return <span className='text-2xl font-bold'>{text}</span>;
    }

    let wordIndex = 0;
    let pendingBlankAnswer = '';
    return (
      <div className='text-2xl font-bold'>
        {sentenceParts.map((part, i) => {
          if (!part.isBlank) {
            if (pendingBlankAnswer) {
              const punct = part.text.replace(/\s/g, '');
              const punctuationOnly = punct.length > 0 && /^[^\w']+$/.test(punct);
              const shouldHide = punctuationOnly && !pendingBlankAnswer.trim().endsWith(punct);
              pendingBlankAnswer = '';
              if (shouldHide) {
                const whitespace = part.text.replace(/[^\s]/g, '');
                return whitespace ? <span key={`sp-${i}`}>{whitespace}</span> : null;
              }
            }
            return <span key={`t-${i}`}>{part.text}</span>;
          }

          const partInfo = parsed.parts && parsed.parts[wordIndex];
          const userAnswer = partInfo ? partInfo.user : '';
          const isCorrect = partInfo ? partInfo.status === 'correct' : false;
          const isEmpty = !userAnswer || userAnswer.trim().length === 0;
          pendingBlankAnswer = userAnswer;
          wordIndex += 1;

          return (
            <span key={`w-wrap-${i}`}>
              <span 
                key={`w-${i}`} 
                className={`inline-block mx-1 px-2 py-1 rounded ${
                  isEmpty 
                    ? 'text-gray-400 bg-gray-200' 
                    : isCorrect 
                      ? 'text-white bg-green-500' 
                      : 'text-white line-through bg-red-500'
                }`}
              >
                {isEmpty ? '___' : userAnswer}
              </span>
            </span>
          );
        })}
      </div>
    );
  };

  // 뉘앙스 부분 영작용: 원문장 안에서 학생 입력을 정오답 색으로 표시 (에세이와 동일 UX)
  const renderNuanceStudentOverlay = () => {
    const text = parsed.fullText || '';
    const correctPart = (parsed.correctAnswerPart || '').trim();
    const studentPhrase = (parsed.answerText && parsed.answerText.trim().length > 0)
      ? parsed.answerText
      : (parsed.selectedWords && parsed.selectedWords.length > 0 ? parsed.selectedWords.join(' ') : '');

    if (!text) return <span className='text-gray-500'>학생 입력이 없습니다.</span>;

    // correctPart가 없으면 전체 문장만 학생 입력 강조 없이 출력
    if (!correctPart) return <span className='text-2xl font-bold'>{text}</span>;

    // 문장에서 정답 부분 위치 찾기 (대소문자 무시)
    const index = text.toLowerCase().indexOf(correctPart.toLowerCase());
    if (index === -1) return <span className='text-2xl font-bold'>{text}</span>;

    const prefix = text.slice(0, index);
    const suffix = text.slice(index + correctPart.length);

    // 단어 단위 비교
    const cWords = correctPart.split(/\s+/).filter(Boolean);
    const sWords = (studentPhrase || '').split(/\s+/).filter(Boolean);
    const normalize = (s: string) => s.replace(/[^a-z0-9']+/gi, '').toLowerCase();
    const maxLen = Math.max(cWords.length, sWords.length);

    return (
      <div className='text-2xl font-bold'>
        <span>{prefix}</span>
        <span>
          {Array.from({ length: maxLen }).map((_, i) => {
            const cw = cWords[i] || '';
            const sw = sWords[i] || '';
            const isEmpty = sw.length === 0;
            const isLastToken = i === maxLen - 1;
            const baseMatch = !isEmpty && normalize(cw) === normalize(sw);
            const isCorrect = baseMatch && (!isLastToken || !hasTrailingPunctuationMismatch(cw, sw));
            const display = isEmpty ? '___' : sw;
            return (
              <span
                key={`nu-${i}`}
                className={
                  isEmpty
                    ? 'px-1 mx-1 text-gray-400'
                    : isCorrect
                      ? 'px-1 mx-1 text-green-700'
                      : 'px-1 mx-1 text-red-600 underline'
                }
              >
                {display}
              </span>
            );
          })}
        </span>
        <span>{suffix}</span>
      </div>
    );
  };
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [writingManualStatus, setWritingManualStatus] = useState<'correct' | 'incorrect' | null>(null);
  const autoAppliedRef = useRef(false);
  const activityKey = `${activity?.id ?? ''}-${responseJson ?? ''}`;

  useEffect(() => {
    autoAppliedRef.current = false;
    if (manualGradingResult === undefined) {
      setWritingManualStatus(null);
    }
  }, [activityKey, manualGradingResult]);
  const [currentDialogStatus, setCurrentDialogStatus] = useState<'correct' | 'incorrect'>('correct');
  const [aiLoading, setAiLoading] = useState(false);

  // 부모의 수동 채점 결과와 동기화 (재렌더/재마운트 시 라디오 체크 유지)
  useEffect(() => {
    if (manualGradingResult === 'correct' || manualGradingResult === 'incorrect') {
      setWritingManualStatus(manualGradingResult);
    } else if (manualGradingResult === undefined) {
      // 부모에서 초기화한 경우 표시도 초기화
      setWritingManualStatus(null);
    }
  }, [manualGradingResult]);

  const handleOpenDialog = () => {
    const current: 'correct' | 'incorrect' = recordingGradingResult || 'correct';
    setCurrentDialogStatus(current);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmGrading = () => {
    onRecordingGradingChange?.(currentDialogStatus);
    handleCloseDialog();
  };

  const handleGradingChange = (status: 'correct' | 'incorrect') => {
    setCurrentDialogStatus(status);
  };

  useEffect(() => {
    if (type !== 'gradingCard') return;
    if (autoAppliedRef.current) return;
    if (manualGradingResult === 'correct' || manualGradingResult === 'incorrect') return;
    const normalized = autoWritingStatus === 'correct' ? 'correct' : 'incorrect';
    setWritingManualStatus(normalized);
    onManualGradingChange?.(normalized);
    autoAppliedRef.current = true;
  }, [autoWritingStatus, type, manualGradingResult, onManualGradingChange]);

  const displayWritingStatus: 'correct' | 'word_incorrect' =
    writingManualStatus ? (writingManualStatus === 'correct' ? 'correct' : 'word_incorrect') : autoWritingStatus;
  const hasRecording = Boolean(parsed.recordingUrl);
  const canGradeRecording = type === 'gradingCard' && hasRecording && !recordingLocked;
  const showRecordingViewButton = hasRecording && (type === 'scoreCard' || recordingLocked);
  const dialogReadOnly = type !== 'gradingCard' || recordingLocked;

  return (
    <div>
    <div className='flex flex-col gap-2'>
        <p className='text-lg font-bold'>빈칸 채우기</p>
        {/* 이미지가 있는 경우에만 프롬프트 미디어 박스를 노출 (이미지 실패/부재 시 전체 박스 제거) */}
        {parsed.imageUrl && (
          <div className='relative p-3 rounded-2xl border border-gray-100'>
            <img
              src={parsed.imageUrl}
              alt=""
              className='rounded-2xl'
              onError={(e) => { (e.target as HTMLImageElement).closest('.relative')?.remove(); }}
            />
            {parsed.audioUrl && (
              <ListenButton audioSrc={parsed.audioUrl} size={40} className='absolute top-2 right-2 !p-1' />
            )}
          </div>
        )}
        {/* 녹음바  녹음 유무에 따라 노출 (채점 화면에서는 숨김) */}
        <div className='relative w-full'></div>

        {/* 녹음한 답 (에세이 빈칸 채우기, 녹음 활성 시에만 표시) */}
        {!parsed.isWordChoiceExtension && parsed.recordingEnabled && (
          <div className='flex justify-between items-center px-5 py-3 bg-gray-50 rounded-2xl border border-gray-200'>
            <p className='text-sm font-bold'>녹음한 답</p>
            <div className='flex flex-wrap gap-2 items-center'>
              {parsed.recordingUrl ? (
                <>
                  {canGradeRecording && (
                    <>
                      {canUseAi && (
                        <Button size='small' onClick={async () => {
                        try {
                          const scheduleId = (window as any).__CURRENT_SCHEDULE_ID__;
                          setAiLoading(true);
                          onRecordingGradingChange?.(undefined);
                          const res = await academyLearningScheduleApi.aiGradeDictation(scheduleId, {
                            recordingUrl: parsed.recordingUrl || '',
                            correctWord: (parsed.correctWords && parsed.correctWords.length > 0) ? parsed.correctWords.join(' ') : (parsed.correctAnswerPart || ''),
                          });
                          const ok = !!res?.data?.correct;
                          onRecordingGradingChange?.(ok ? 'correct' : 'incorrect');
                        } catch (e) {
                          console.error('PART_WRITING AI 채점 실패', e);
                          alert('AI 채점에 실패했습니다.');
                        } finally {
                          setAiLoading(false);
                        }
                      }} disabled={aiLoading}>{aiLoading ? '채점중...' : 'AI 채점'}</Button>
                      )}
                      <Button size='small' intent='secondary' onClick={handleOpenDialog}>녹음 듣고 채점하기</Button>
                    </>
                  )}
                  {showRecordingViewButton && (
                    <Button size='small' intent='secondary' onClick={handleOpenDialog}>녹음 듣기</Button>
                  )}
                </>
              ) : (
                <span className='text-sm text-gray-500'>제출된 녹음이 없습니다.</span>
              )}
              <div className='w-[70px] flex-none flex items-center justify-center'>
                <GradingState status={
                  parsed.recordingUrl
                    ? (recordingGradingResult === 'correct'
                        ? 'correct'
                        : recordingGradingResult === 'incorrect'
                          ? 'recording_incorrect'
                          : 'grading')
                    : 'grading'
                } />
              </div>
            </div>
          </div>
        )}

        <div>
                <p className='text-sm font-bold text-gray-500'>보기</p>
                <div className='flex justify-center items-center p-5 bg-gray-50 rounded-2xl'>
                    <div>
                      {renderEssayPrompt()}
                    </div>
                </div>
            </div>

            <div className={`overflow-hidden rounded-2xl border ${displayWritingStatus === 'correct' ? 'border-green-500' : displayWritingStatus === 'word_incorrect' ? 'border-red-500' : 'border-gray-200'}`}>
                <div className='flex justify-between items-center px-4 py-2'>
                    <p className='text-sm font-bold text-gray-500'>학생 입력</p>
                    <div className='flex gap-3 items-center'>
                      <GradingState status={displayWritingStatus} />
                    </div>
                </div>
                <div className='flex justify-center items-center p-5 text-center bg-gray-50'>
                    {parsed.isWordChoiceExtension ? renderNuanceStudentOverlay() : renderEssayStudentAnswers()}
                </div>
            </div>
    </div>
    <Dialog
                open={isDialogOpen}
                onCancel={handleCloseDialog}
                onOk={dialogReadOnly ? handleCloseDialog : handleConfirmGrading}
                okText={dialogReadOnly ? '닫기' : '확인'}
                cancelText={dialogReadOnly ? undefined : '취소'}
            >
                <RecordingGradingContents 
                  word={parsed.fullText || '문장'}
                  recordingUrl={parsed.recordingUrl || undefined}
                  initialGradingStatus={currentDialogStatus}
                  onGradingChange={dialogReadOnly ? undefined : handleGradingChange}
                  readOnly={dialogReadOnly}
                />
            </Dialog>
</div>
  )
}


export default GradingPartWriting